import { useCallback, useState } from 'react';
import { TextField, AnchorField } from 'framework/ui/field';
import { Grid2 as Grid, Stack, Typography } from '@mui/material';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import FTEField from './fields/fteFields/FTEField';
import BillingTypeField from './fields/BillingTypeField';
import ProjectSowNameField from './fields/sowFields/ProjectSowNameField';
import TotalHeader from './fields/totalFields/TotalHeader';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import IconButton from '@mui/material/IconButton';
import TotalField from './fields/totalFields/TotalField';
import BudgetAmountField from './fields/BudgetAmountField';
import BudgetSourceField from './fields/BudgetSourceField';
import { getWithThousandsSeparator } from 'framework/utils';
import BudgetDateField from './fields/BudgetDateField';
import { EditFormShowButton } from 'framework/form';
import { useNavigate } from 'react-router-dom';
import { useAuthorization } from 'framework/authorization';
import BudgetHoursField from './fields/BudgetHoursField';
import {
  useProjectDataContext,
  ACCESS_INFO_ACTIONS,
  ACCESS_INFO_SECTIONS,
  useProjectSecurityContext,
  BUDGET_UPDATE,
  useNewProjectUrl,
  ProjectSectionAccordion,
} from 'domain/shared/newProject';
import {
  EditableListDataGridLayout,
  EDITABLE_LIST_CUSTOM,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
} from 'framework/list';

const PeriodicBasedBudget = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { isGranted } = useAuthorization();
  const { data } = useProjectDataContext();
  const { isAccessGranted } = useProjectSecurityContext();

  const { getUpdateBudgetUrl } = useNewProjectUrl();
  const navigate = useNavigate();

  const toggleVisible = (newStateVisible: boolean) => {
    setIsVisible(newStateVisible);
  };

  const renderActions = useCallback(() => {
    if (!isGranted(BUDGET_UPDATE) && !isAccessGranted(ACCESS_INFO_SECTIONS.BUDGET, ACCESS_INFO_ACTIONS.MANAGE)) {
      return [];
    }
    return [
      <IconButton sx={{ p: 0, ml: 1 }} key="add" onClick={() => navigate(getUpdateBudgetUrl(data.id!))}>
        <EditFormShowButton />
      </IconButton>,
    ];
  }, [getUpdateBudgetUrl, data, isGranted, isAccessGranted, navigate]);

  return (
    <ProjectSectionAccordion
      defaultExpanded
      title={
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Typography variant="h4" color="primary" pl={2}>
              Budget
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1}>
            {isVisible && (
              <IconButton onClick={() => toggleVisible(false)} size="small">
                <VisibilityIcon sx={{ color: 'primary.main' }} />
              </IconButton>
            )}
            {!isVisible && (
              <IconButton onClick={() => toggleVisible(true)} size="small">
                <VisibilityOff sx={{ color: 'primary.main' }} />
              </IconButton>
            )}
            <>{renderActions()}</>
          </Stack>
        </Stack>
      }
    >
      <EditableListDataGridLayout
        rerenderTableCellsDeps={[isVisible]}
        getTableCells={isEditModeLayout =>
          Promise.resolve([
            {
              type: EDITABLE_LIST_GROUP,
              component: Stack,
              componentProps: {
                spacing: 1,
              },
              gridCellProps: {
                size: 14,
              },
              children: [
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'id',
                      isEditor: false,
                      component: AnchorField,
                      gridCellProps: { size: 0.5 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'billingType',
                      isEditor: false,
                      component: BillingTypeField,
                      componentProps: { isPrestart: 'state' },
                      gridCellProps: { size: 3, alignItems: 'start' },
                    },
                    {
                      type: EDITABLE_LIST_NESTED_LIST,
                      source: 'structure',
                      gridCellProps: { size: 8.5 },
                      component: Grid,
                      componentProps: { direction: 'row', container: true },
                      children: [
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetAmountField,
                          componentProps: {
                            source: 'finance',
                            status: 'finance.status',
                            showContent: isVisible,
                          },
                          gridCellProps: { size: 2, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetHoursField,
                          componentProps: {
                            source: 'budget.hours',
                            status: 'finance.status',
                          },
                          gridCellProps: { size: 2, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetDateField,
                          componentProps: {
                            source: 'source',
                            date: 'budget.effectiveStartDate',
                            fallbackDate: 'finance.createdAt',
                            sowDate: 'budget.effectiveStartDate',
                            tempDate: 'budgetStartDate',
                          },
                          gridCellProps: { size: 3, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_CUSTOM,
                          isEditor: false,
                          component: BudgetDateField,
                          componentProps: {
                            source: 'source',
                            date: 'budget.effectiveEndDate',
                            sowDate: 'budget.effectiveEndDate',
                            tempDate: 'budgetEndDate',
                          },
                          gridCellProps: { size: 3, alignItems: 'start' },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'budget',
                          isEditor: false,
                          component: BudgetSourceField,
                          componentProps: {
                            financeBudgetSource: 'finance',
                          },
                          gridCellProps: {
                            size: 2,
                            alignItems: 'start',
                            justifyContent: 'flex-end',
                            display: 'flex',
                            sx: {
                              '& ul': {
                                justifyContent: 'flex-end',
                              },
                            },
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                    fontWeight: 800,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: Typography,
                      gridCellProps: {
                        size: 0.5,
                      },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: TotalHeader,
                      gridCellProps: {
                        size: 3,
                      },
                    },
                    {
                      type: EDITABLE_LIST_GROUP,
                      component: Grid,
                      componentProps: {
                        container: true,
                        direction: 'row',
                      },
                      gridCellProps: { size: 8.5 },
                      children: [
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'structure',
                          component: TotalField,
                          isEditor: false,
                          componentProps: {
                            field: 'amount',
                            showContent: isVisible,
                            calculateResult: (budgetData: any[], financeData: any[], showContent: boolean) => {
                              if (!showContent) return '*****';
                              return `${getWithThousandsSeparator(financeData.reduce((sum, val) => sum + (Number(val) || 0), 0))} USD`;
                            },
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          isEditor: false,
                          source: 'structure',
                          component: TotalField,
                          componentProps: {
                            field: 'hours',
                            showContent: isVisible,
                            calculateResult: (budgetData: object[]) => {
                              return `${budgetData.reduce((sum, val) => sum + (Number(val) || 0), 0).toFixed(2)} h`;
                            },
                          },
                          gridCellProps: {
                            size: 2,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          isEditor: false,
                          source: 'startDate',
                          component: TextField,
                          componentProps: {
                            className: { total: { fontWeight: 800 } },
                          },
                          gridCellProps: {
                            size: 3,
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'endDate',
                          isEditor: false,
                          component: TextField,
                          componentProps: {
                            className: { total: { fontWeight: 800 } },
                          },
                          gridCellProps: {
                            size: 5,
                          },
                        },
                      ],
                    },
                  ],
                },
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Stack,
                  componentProps: {
                    spacing: 1,
                    direction: 'row',
                  },
                  gridCellProps: {
                    size: 12,
                    borderTop: '1px solid var(--mui-palette-border-default)',
                    borderBottom: '1px solid var(--mui-palette-border-default)',
                    py: 1,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: Typography,
                      gridCellProps: { size: 0.5 },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: ProjectSowNameField,
                      componentProps: {
                        sowIdSource: 'sowId',
                        projectNameSource: 'projectName',
                      },
                      gridCellProps: { size: 3 },
                    },
                    {
                      type: EDITABLE_LIST_CUSTOM,
                      isEditor: false,
                      component: FTEField,
                      componentProps: {
                        fteSource: 'fte',
                      },
                      gridCellProps: {
                        size: 8.5,
                        sx: { '.MuiFormControl-root': { mb: 0 } },
                      },
                    },
                  ],
                },
              ],
            },
          ])
        }
      />
    </ProjectSectionAccordion>
  );
};

export default PeriodicBasedBudget;
